import ArrowUpDownIcon from '@assets/icons/arrow-up-down.svg';
import DollarSignIcon from '@assets/icons/dollar-sign.svg';
import EmployeesIcon from '@assets/icons/employees.svg';
import i18n from '@/utils/i18n';
import { Icons } from '@usepepper/sprout-kit-ui';
import { navigateToSalesPortalRoute } from '@/experiences/sales-portal/sales-portal-route-config';
import {
  NavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import {
  SALES_PORTAL_ROUTES,
  SALES_PORTAL_TAB_ROUTES,
} from '@/navigators/routes';
import { SalesPortalPricingStackParams } from '@/navigators/types';
import {
  selectBookmarkCount,
  usePricingBookmarksStore,
} from '@/experiences/sales-portal/domain/pricing/blocs/stores/pricing-bookmarks-store';
import { SidenavItem, SidenavSingleItem } from '@/domain/navigation/types';
import { useBusinessOrganizationPricingConfig } from '@/experiences/sales-portal/blocs/hooks/use-business-organization-pricing-config';
import { useMemo } from 'react';
import { usePricingBookmarksLoader } from '@/experiences/sales-portal/domain/pricing/blocs/loaders/use-pricing-bookmarks-loader';
import { usePricingMicroProducts } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-micro-products';

const PRICING_ACTIVE_ROUTES = new Set([
  SALES_PORTAL_ROUTES.PricingAgentReview,
  SALES_PORTAL_ROUTES.PricingBookmarked,
  SALES_PORTAL_ROUTES.PricingCustomerDetail,
  SALES_PORTAL_ROUTES.PricingCustomers,
  SALES_PORTAL_ROUTES.PricingItemManagement,
  SALES_PORTAL_ROUTES.PricingManagement,
  SALES_PORTAL_ROUTES.PricingManagementReview,
  SALES_PORTAL_ROUTES.PricingMicroProduct,
  SALES_PORTAL_ROUTES.PricingOverview,
]);

const CUSTOMERS_ACTIVE_ROUTES = new Set([
  SALES_PORTAL_ROUTES.PricingCustomerDetail,
  SALES_PORTAL_ROUTES.PricingCustomers,
]);

const COST_CHANGES_ACTIVE_ROUTES = new Set([
  SALES_PORTAL_ROUTES.PricingItemManagement,
  SALES_PORTAL_ROUTES.PricingManagement,
  SALES_PORTAL_ROUTES.PricingManagementReview,
]);

export const usePricingSidebarNavItems = (): SidenavItem[] => {
  const route = useRoute();
  const navigation =
    useNavigation<NavigationProp<SalesPortalPricingStackParams>>();
  const currentParams = (route.params ?? {}) as Record<string, unknown>;
  const {
    hasBookmarkingAccess,
    hasPricingAccess,
    hasCustomerTableAccess,
    hasPricingAgentAccess,
  } = useBusinessOrganizationPricingConfig();
  const { products: microProducts } = usePricingMicroProducts();
  const currentProductId = currentParams.productId as string | undefined;

  // Fires GetPricingBookmarks (once, cache-first) and hydrates the bookmark
  // store so row icons and this count reflect saved bookmarks on load.
  usePricingBookmarksLoader();
  const bookmarkCount = usePricingBookmarksStore(selectBookmarkCount);

  return useMemo(() => {
    if (!hasPricingAccess) {
      return [];
    }

    const goToCostChanges = () => {
      navigateToSalesPortalRoute(
        navigation,
        SALES_PORTAL_ROUTES.PricingManagement,
      );
    };

    const isPricingActive = PRICING_ACTIVE_ROUTES.has(
      route.name as SALES_PORTAL_ROUTES,
    );

    const subnavItems: SidenavSingleItem[] = [
      {
        active: route.name === SALES_PORTAL_ROUTES.PricingOverview,
        icon: Icons.LayoutDashboard,
        id: SALES_PORTAL_ROUTES.PricingOverview,
        label: i18n.t('pricing.overview.sidenav_title'),
        onPress: () => {
          navigateToSalesPortalRoute(
            navigation,
            SALES_PORTAL_ROUTES.PricingOverview,
          );
        },
      },
      {
        active: COST_CHANGES_ACTIVE_ROUTES.has(
          route.name as SALES_PORTAL_ROUTES,
        ),
        icon: ArrowUpDownIcon,
        id: SALES_PORTAL_ROUTES.PricingManagement,
        label: i18n.t('pricing.cost_changes.sidenav_title'),
        onPress: goToCostChanges,
      },
    ];

    if (hasCustomerTableAccess) {
      subnavItems.push({
        active: CUSTOMERS_ACTIVE_ROUTES.has(route.name as SALES_PORTAL_ROUTES),
        icon: EmployeesIcon,
        id: SALES_PORTAL_ROUTES.PricingCustomers,
        label: i18n.t('pricing.customers.sidenav_title'),
        onPress: () => {
          navigateToSalesPortalRoute(
            navigation,
            SALES_PORTAL_ROUTES.PricingCustomers,
          );
        },
      });
    }

    if (hasPricingAgentAccess) {
      subnavItems.push({
        active:
          route.name === SALES_PORTAL_ROUTES.PricingAgentTasks ||
          route.name === SALES_PORTAL_ROUTES.PricingAgentReview,
        icon: Icons.Sparkles,
        id: SALES_PORTAL_ROUTES.PricingAgentTasks,
        label: i18n.t('pricing.agent.sidenav_title'),
        onPress: () => {
          navigateToSalesPortalRoute(
            navigation,
            SALES_PORTAL_ROUTES.PricingAgentTasks,
          );
        },
      });
    }

    if (hasBookmarkingAccess && bookmarkCount > 0) {
      subnavItems.push({
        active: route.name === SALES_PORTAL_ROUTES.PricingBookmarked,
        icon: Icons.Bookmark,
        id: SALES_PORTAL_ROUTES.PricingBookmarked,
        label: i18n.t('pricing.bookmarks.sidenav_title'),
        onPress: () => {
          navigateToSalesPortalRoute(
            navigation,
            SALES_PORTAL_ROUTES.PricingBookmarked,
          );
        },
      });
    }

    // Server-driven pricing microproducts (PRICING_SIDEBAR slot). Labels/icons
    // come from the API, so no i18n. The group already requires
    // hasPricingAccess, so these never dead-end at NotFound.
    microProducts.forEach((product) => {
      subnavItems.push({
        active:
          route.name === SALES_PORTAL_ROUTES.PricingMicroProduct &&
          currentProductId === product.id,
        iconUrl: product.displayIconUrl ?? undefined,
        id: `${SALES_PORTAL_ROUTES.PricingMicroProduct}:${product.id}`,
        label: product.displayName,
        onPress: () => {
          navigateToSalesPortalRoute(
            navigation,
            SALES_PORTAL_ROUTES.PricingMicroProduct,
            { productId: product.id },
          );
        },
      });
    });

    return [
      {
        active: isPricingActive,
        icon: DollarSignIcon,
        id: SALES_PORTAL_TAB_ROUTES.Pricing,
        label: i18n.t('pricing'),
        subnavItems,
      },
    ];
  }, [
    bookmarkCount,
    currentProductId,
    hasBookmarkingAccess,
    hasCustomerTableAccess,
    hasPricingAccess,
    hasPricingAgentAccess,
    microProducts,
    navigation,
    route.name,
  ]);
};
